import {
  Box,
  VStack,
  Button,
  Text,
  Checkbox,
  Link,
  HStack,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { LockIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useProposalStore } from "@/contexts/useProposalStore";
import { usePersonalDataStore } from "@/contexts/usePersonalDataStore";
import { useGeolocationStore } from "@/contexts/useGeolocationStore";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { useConditionalBackground } from "@/contexts/useConditionalBackground ";
import { useState, useMemo, useEffect } from "react";
import { ProposalConsignado } from "@/models/proposalConsignado.model";
import { 
  acceptConsignadoProposal, 
  getConsignadoDocumentTerms,
  DocumentTermType 
} from "@/services/proposal.service";

interface InsuranceOffer {
  id?: string | null;
  id_empresa_seguradora?: string | null;
  id_externo?: string | null;
  valor_seguro?: string | number | null;
  valor_emprestimo?: string | number | null;
  numero_parcelas?: string | number | null;
}

interface FinancialOffer {
  id: string;
  processorName?: string;
  numeroParcelas: number;
  valorParcela: number;
  valorLiberado: number;
  valorTaxaMensal?: number;
  valorSeguro?: number;
  valorTotalDivida: number;
  comSeguro: boolean;
}

interface OfferAcceptance {
  insuranceDiscount: boolean;
  insurancePolicy: boolean;
  contract: boolean;
}

export default function ConsigandoConfirmation() {
  const { proposal } = useProposalStore();
  const { telefone, email, emailEmpresa, telefoneEmpresa } = usePersonalDataStore();
  const { latitude, longitude } = useGeolocationStore();

  // console.log("proposal", proposal);
  const backgroundStyle = useConditionalBackground();
  const navigate = useNavigate();
  const toast = useToast();

  const consignadoProposal = proposal as ProposalConsignado;
  const insuranceInfo: InsuranceOffer | null | undefined =
    consignadoProposal?.informacoes_seguro;

  const financialOffers: FinancialOffer[] =
    consignadoProposal?.infromacoes_financeiras || [];

  // Ordena as ofertas: primeiro as com seguro, depois as sem seguro
  const sortedFinancialOffers = useMemo(() => {
    return [...financialOffers].sort((a, b) => {
      const aHasInsurance = a.comSeguro;
      const bHasInsurance = b.comSeguro;

      // Se a tem seguro e b não, a vem primeiro
      if (aHasInsurance && !bHasInsurance) return -1;
      // Se b tem seguro e a não, b vem primeiro
      if (!aHasInsurance && bHasInsurance) return 1;
      // Se ambos têm ou não têm seguro, mantém a ordem original
      return 0;
    });
  }, [financialOffers]);

  const isNoInsuranceOfferUnavailable = (offer: FinancialOffer): boolean => {
    if (offer.comSeguro) {
      return false;
    }

    return [
      offer.valorLiberado,
      offer.valorParcela,
      offer.valorSeguro,
      offer.valorTaxaMensal,
      offer.valorTotalDivida,
    ].every((value) => Number(value ?? 0) === 0);
  };

  // Estado para gerenciar os checkboxes de cada oferta
  const [acceptances, setAcceptances] = useState<
    Record<string, OfferAcceptance>
  >({});

  // Estado para controlar qual card está selecionado
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  // Estado para controlar loading
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});

  // Verifica se alguma oferta está sendo processada
  const isAnySubmitting = Object.values(isSubmitting).some((value) => value);

  // Inicializa com o card "Com Seguro" selecionado por padrão
  useEffect(() => {
    // console.log("sortedFinancialOffers", sortedFinancialOffers);
    if (sortedFinancialOffers.length === 0) {
      return;
    }

    const selectedOffer = selectedOfferId
      ? sortedFinancialOffers.find((offer) => offer.id === selectedOfferId)
      : null;

    if (selectedOffer && !isNoInsuranceOfferUnavailable(selectedOffer)) {
      return;
    }

    const firstAvailableOffer = sortedFinancialOffers.find(
      (offer) => !isNoInsuranceOfferUnavailable(offer)
    );

    if (firstAvailableOffer) {
      setSelectedOfferId(firstAvailableOffer.id);
    } else {
      setSelectedOfferId(null);
    }
  }, [sortedFinancialOffers, selectedOfferId]);

  if (!consignadoProposal) {
    navigate("/404");
    return null;
  }

  const getValues = (financialOffer: FinancialOffer) => {
    return {
      numeroParcelas: parseFloat(String(financialOffer.numeroParcelas || "0")),
      valorDevido: parseFloat(String(financialOffer.valorTotalDivida || "0")),
      valorSeguro: parseFloat(String(financialOffer.valorSeguro || "0")),
      valorParcela: parseFloat(String(financialOffer.valorParcela || "0")),
      valorLiberado: financialOffer.valorLiberado,
    };
  };

  const updateAcceptance = (
    offerId: string,
    field: keyof OfferAcceptance,
    value: boolean
  ) => {
    setAcceptances((prev) => ({
      ...prev,
      [offerId]: {
        ...prev[offerId],
        [field]: value,
      },
    }));
  };

  const getAcceptance = (offerId: string): OfferAcceptance => {
    return (
      acceptances[offerId] || {
        insuranceDiscount: false,
        insurancePolicy: false,
        contract: false,
      }
    );
  };

  const isOfferReadyToSign = (offerId: string): boolean => {
    const acceptance = getAcceptance(offerId);
    return acceptance.contract;
  };

  const handleSelectOffer = (offerId: string) => {
    if (selectedOfferId === offerId) {
      setSelectedOfferId(null);
    } else {
      setSelectedOfferId(offerId);
    }
  };

  const handleAssinar = async (
    financialOfferId: string,
    hasInsuranceValue: boolean
  ) => {
    if (!telefone || !email) {
      toast({
        title: "Dados incompletos",
        description: "Telefone e email são obrigatórios.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting((prev) => ({ ...prev, [financialOfferId]: true }));
    try {
      const payload = hasInsuranceValue
        ? {
            telefone,
            email,
            id_financial_info: financialOfferId,
            id_insurance_info: insuranceInfo?.id,
            latitude: latitude || undefined,
            longitude: longitude || undefined,
            emailEmpresa: emailEmpresa || undefined,
            telefoneEmpresa: telefoneEmpresa || undefined,
          }
        : {
            telefone,
            email,
            id_financial_info: financialOfferId,
            id_insurance_info: undefined,
            latitude: latitude || undefined,
            longitude: longitude || undefined,
            emailEmpresa: emailEmpresa || undefined,
            telefoneEmpresa: telefoneEmpresa || undefined,
          };

      await acceptConsignadoProposal(consignadoProposal.proposal_id, payload);
      // Redireciona para o fluxo da Único após sucesso
      // Verifica unicoId na resposta da API, na proposta original, ou unico_id (snake_case)
      const unicoId = consignadoProposal.unico_id;

      if (unicoId) {
        const redirectUrl =
          import.meta.env.VITE_ENV === "local"
            ? `https://cadastro.uat.unico.app/process/${unicoId}`
            : `https://cadastro.unico.app/process/${unicoId}`;

        window.location.href = redirectUrl;
      } else {
        console.warn("unicoId não encontrado, exibindo toast de sucesso");
        toast({
          title: "Sucesso",
          description: "Proposta aceita com sucesso!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao aceitar a proposta. Tente novamente.";
      toast({
        title: "Erro",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [financialOfferId]: false }));
    }
  };

  const handleDownloadDocument = async (
    type: DocumentTermType,
    financialInfoId: string,
    insuranceInfoId?: string
  ) => {
    try {
      const response = await getConsignadoDocumentTerms({
        type,
        proposalId: consignadoProposal.proposal_id,
        financialInfoId,
        insuranceInfoId,
      });

      // 1. Cria o Blob diretamente com a resposta
      // Nota: Se a resposta vier vazia, pode gerar um PDF em branco, vale validar.
      const blob = new Blob([response], { type: "application/pdf" });

      // 2. Cria a URL temporária
      const url = window.URL.createObjectURL(blob);
      
      // 3. Cria o link e força o clique (padrão)
      const link = document.createElement("a");
      link.href = url;
      // Define o nome do arquivo
      link.download = `${type}.pdf`; 
      
      document.body.appendChild(link);
      link.click();
      
      // 4. Limpeza
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      // DICA: Se der erro no backend, como definimos responseType: 'blob',
      // o erro virá como um Blob também, e não como JSON texto.
      // Para ler a mensagem de erro real, precisamos converter o blob de volta para texto.
      
      let errorMessage = "Erro ao baixar o documento. Tente novamente.";

      if (error?.response?.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          // Se não for JSON, mantém a mensagem padrão
        }
      } else {
        errorMessage = error?.message || errorMessage;
      }

      toast({
        title: "Erro",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      minHeight="100vh"
      {...backgroundStyle}
      display="flex"
      flexDirection="column"
      alignItems="center"
      px={4}
      py={6}
      pb={20}
    >
      {/* Título Principal */}
      <VStack
        spacing={4}
        bg="white"
        p={3}
        mt={4}
        borderRadius="lg"
        boxShadow="lg"
        width="100%"
        align="start"

        color="gray.800"
        fontFamily="Lexend Deca"
      >
        <VStack p={2} width="100%" align="start">
          <Text
            fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
            fontWeight="bold"
            color="primary"
            textAlign="center"
            width="100%"
          >
            Escolha a modalidade do seu empréstimo
          </Text>
        </VStack>
        
        {sortedFinancialOffers.map((financialOffer) => {
          const hasInsuranceValue = financialOffer.comSeguro;
          const isOfferLocked = isNoInsuranceOfferUnavailable(financialOffer);
          const values = getValues(financialOffer);

          if (isOfferLocked) {
            return (
              <VStack
                key={financialOffer.id}
                spacing={0}
                width="100%"
                align="start"
                border="2px dashed"
                borderColor="gray.200"
                borderRadius="2xl"
                overflow="hidden"
                bg="gray.50"
                cursor="not-allowed"
              >
                <Box width="100%" bg="gray.200" py={3} textAlign="center">
                  <Text
                    fontSize={{ base: "lg", sm: "xl" }}
                    fontWeight="bold"
                    color="gray.700"
                    fontFamily="Lexend Deca"
                  >
                    Sem seguro
                  </Text>
                </Box>

                <VStack width="100%" spacing={4} px={6} py={6} align="center">
                  <LockIcon boxSize={12} color="gray.500" />

                  <Text
                    fontSize={{ base: "2xl", sm: "3xl" }}
                    fontWeight="bold"
                    color="gray.700"
                    textAlign="center"
                    fontFamily="Lexend Deca"
                    lineHeight="1.1"
                  >
                    Indisponivel para seu perfil
                  </Text>

                  <VStack as="ul" align="start" spacing={2} width="100%" maxW="420px">
                    {/* <Text as="li" fontSize={{ base: "md", sm: "lg" }} color="gray.600">
                      Vinculo CLT &lt; 24 meses
                    </Text> */}
                    <Text as="li" fontSize={{ base: "md", sm: "lg" }} color="gray.600">
                      Renda menor que a permitida
                    </Text>
                    {/* <Text as="li" fontSize={{ base: "md", sm: "lg" }} color="gray.600">
                      Empregador de pequeno porte ou atividade recente
                    </Text> */}
                  </VStack>
                </VStack>
              </VStack>
            );
          }

          return (
            <VStack
              key={financialOffer.id}
              spacing={2}
              width="100%"
              align="start"
              border="2px solid"
              borderColor={
                selectedOfferId === financialOffer.id
                  ? hasInsuranceValue
                    ? "green.500"
                    : "gray.400"
                  : "gray.300"
              }
              bg={
                selectedOfferId === financialOffer.id
                  ? hasInsuranceValue
                    ? "green.50"
                    : "gray.50"
                  : "white"
              }
              borderRadius="lg"
              p={4}
              transition="all 0.2s"
              opacity={isOfferLocked ? 0.65 : 1}
              cursor={isOfferLocked ? "not-allowed" : "pointer"}
              onClick={() => {
                if (isOfferLocked) return;
                handleSelectOffer(financialOffer.id);
              }}
            >
              <HStack width="100%"  justify="space-between" align="center" flexWrap="wrap" mb={2}>
                <HStack spacing={3} align="center">
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isDisabled={isOfferLocked}
                      isChecked={selectedOfferId === financialOffer.id}
                      onChange={() => {
                        if (isOfferLocked) return;
                        handleSelectOffer(financialOffer.id);
                      }}
                      colorScheme={hasInsuranceValue ? "green" : "gray"}
                      size={hasInsuranceValue ? "lg" : "md"}
                    />
                  </Box>
                  <Text
                    fontSize={hasInsuranceValue ? { base: "md", sm: "lg", md: "xl" } : { base: "sm", sm: "md", md: "lg" }}
                    width={120}
                    fontWeight={hasInsuranceValue ? "bold" : "semibold"}
                    color={hasInsuranceValue ? "gray.800" : "gray.600"}
                    fontFamily="Lexend Deca"
                  >
                    {hasInsuranceValue ? "Com Seguro" : "Sem Seguro"}
                  </Text>
                </HStack>
                {financialOffer.valorTaxaMensal && (
                  <Box
                    bg={hasInsuranceValue ? "green.400" : "red.100"}
                    border={
                      hasInsuranceValue
                        ? "1px solid green.900"
                        : "1px solid red.900"
                    }
                    px={2}
                    py={1}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Text
                      fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                      fontWeight="bold"
                      color={hasInsuranceValue ? "white" : "red.600"}
                      fontFamily="Lexend Deca"
                    >
                      Juros:
                    </Text>
                    <Text
                       fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                       fontWeight="bold"
                       color={hasInsuranceValue ? "white" : "red.600"}
                       fontFamily="Lexend Deca"
                    >
                      {financialOffer.valorTaxaMensal.toFixed(2)}% A.M
            </Text>
            </Box>
                )}
              </HStack>
              {/* Informações Financeiras */}
              {hasInsuranceValue ? (
                // Layout com seguro
                <VStack spacing={3} width="100%" align="start">
                  <HStack
                    width="100%"
                    justify="space-between"
                    align="start"
                    sx={{
                      "@media screen and (max-width: 315px)": {
                        flexDirection: "column",
                      },
                    }}
                    // flexWrap="wrap"
                    gap={{ base: 3, sm: 4 }}
                  >
                    <VStack align="start" flex="1 1 0" spacing={1}>
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.500">
                        Valor Liberado
                      </Text>
                      <Text fontSize={{ base: "xl", sm: "2xl", md: "3xl" }} fontWeight="bold" color="gray.800">
                        {BrlCurrencyFormatter(values.valorLiberado)}
                      </Text>
                    </VStack>
                    <VStack
                      spacing={1}
                      flex="1 1 0"
                      minW="110px"
                      sx={{
                        alignItems: "flex-end",
                        "@media screen and (max-width: 315px)": {
                          alignItems: "flex-start",
                        },
                      }}
                    >
                      <Text 
                        fontSize={{ base: "xs", sm: "sm" }} 
                        color="gray.500"
                        width="100%"
                        sx={{
                          textAlign: "end",
                          "@media screen and (max-width: 315px)": {
                            textAlign: "start",
                          },
                        }}
                      >
                        Total de Parcelas
                      </Text>
                      <Text
                        fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
                        width="100%"
                        fontWeight="bold"
                        color="gray.800"
                        sx={{
                          textAlign: "end",
                          "@media screen and (max-width: 315px)": {
                            textAlign: "start",
                          },
                        }}
                      >
                      {values.numeroParcelas}
                      </Text>
                    </VStack>
                  </HStack>

                  <Divider borderColor="gray.300" />

                  <VStack spacing={2} width="100%" align="start" pt={2}>
                    <HStack width="100%" justify="space-between">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                        Valor Parcelas
                      </Text>
                      <Text fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="medium" color="gray.800">
                        {BrlCurrencyFormatter(values.valorParcela)}
                      </Text>
                    </HStack>

                    <HStack width="100%" justify="space-between">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                        Valor Devido
                      </Text>
                      <Text fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="medium" color="gray.800">
                        {BrlCurrencyFormatter(values.valorDevido)}
                      </Text>
                    </HStack>
{/* 
                    <HStack width="100%" justify="space-between">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                        Valor Seguro
                      </Text>
                      <Text fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="medium" color="gray.800">
                        {BrlCurrencyFormatter(values.valorSeguro)}
                      </Text>
                    </HStack> */}
                  </VStack>
                </VStack>
              ) : (
                // Layout sem seguro - mesmo estilo do com seguro
                <VStack spacing={3} width="100%" align="start">
                  <HStack
                    width="100%"
                    justify="space-between"
                    align="start"
                    sx={{
                      "@media screen and (max-width: 315px)": {
                        flexDirection: "column",
                      },
                    }}
                    gap={{ base: 3, sm: 4 }}
                  >
                    <VStack align="start" flex="1 1 0" spacing={1} minW="110px">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.500">
                        Valor Liberado
                      </Text>
                      <Text fontSize={{ base: "lg", sm: "xl", md: "2xl" }} fontWeight="bold" color="gray.800">
                        {BrlCurrencyFormatter(values.valorLiberado)}
                      </Text>
                    </VStack>
                    <VStack
                      spacing={1}
                      sx={{
                        alignItems: "flex-end",
                        "@media screen and (max-width: 315px)": {
                          alignItems: "flex-start",
                        },
                      }}
                    >
                      <Text 
                        fontSize={{ base: "xs", sm: "sm" }} 
                        color="gray.500"
                        width="100%"
                        sx={{
                          textAlign: "end",
                          "@media screen and (max-width: 315px)": {
                            textAlign: "start",
                          },
                        }}
                      >
                        Total de Parcelas
                      </Text>
                      <Text
                        fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
                        width="100%"
                        fontWeight="bold"
                        color="gray.800"
                        sx={{
                          textAlign: "end",
                          "@media screen and (max-width: 315px)": {
                            textAlign: "start",
                          },
                        }}
                      >
                      {values.numeroParcelas}
                      </Text>
                    </VStack>
                  </HStack>

                  <Divider borderColor="gray.300" />

                  <VStack spacing={2} width="100%" align="start" pt={2}>
                    <HStack width="100%" justify="space-between">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                        Valor Parcelas
                      </Text>
                      <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="medium" color="gray.700">
                      {BrlCurrencyFormatter(values.valorParcela)}
                      </Text>
                    </HStack>

                    <HStack width="100%" justify="space-between">
                      <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                        Valor Devido
                      </Text>
                      <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="medium" color="gray.700">
                        {BrlCurrencyFormatter(values.valorDevido)}
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              )}
            </VStack>
          );
        })}

        {/* Card de Critérios de Aceite - Separado e Dinâmico */}
        {selectedOfferId &&
          (() => {
            const selectedOffer = sortedFinancialOffers.find(
              (offer) => offer.id === selectedOfferId
            );
            if (!selectedOffer) return null;

            const hasInsuranceValue = selectedOffer.comSeguro;
            const acceptance = getAcceptance(selectedOfferId);

            return (
              <VStack
                width="100%"
                align="start"
                border="2px solid"
                borderColor="gray.300"
                bg="gray.50"
                borderRadius="lg"
                p={5}
                onClick={() => {
                  const newValue = !acceptance.contract;
                  updateAcceptance(selectedOfferId, "contract", newValue);
                }}
              >
                {/* Checkboxes de Aceite */}
                <HStack 
                  align="flex-start" 
                  spacing={3} 
                  width="100%"
                >
                  <Checkbox
                    isChecked={acceptance.contract}
                    onChange={(e) =>
                      updateAcceptance(
                        selectedOfferId,
                        "contract",
                        e.target.checked
                      )
                    }
                    colorScheme="green"
                    size="md"
                    mt={1}
                  />
                  <VStack align="start" spacing={2} flex={1}>
                    <Text fontSize={{ base: "sm", sm: "md" }} fontWeight="bold" color="gray.800">
                      Li e estou de acordo com:
                    </Text>
                    {hasInsuranceValue ? (
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2} align="flex-start">
                          <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.700">•</Text>
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadDocument(
                                "apolice",
                                selectedOfferId,
                                insuranceInfo?.id || undefined
                              );
                            }}
                            textDecoration="underline"
                            color="green.500"
                            fontWeight="semibold"
                            cursor="pointer"
                            fontSize={{ base: "xs", sm: "sm" }}
                          >
                            1. Apólice Seguro
                          </Link>
                        </HStack>
                        <HStack spacing={2} align="flex-start">
                          <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.700">•</Text>
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadDocument(
                                "contrato-ccb",
                                selectedOfferId,
                                insuranceInfo?.id || undefined
                              );
                            }}
                            textDecoration="underline"
                            color="green.500"
                            fontWeight="semibold"
                            cursor="pointer"
                            fontSize={{ base: "xs", sm: "sm" }}
                          >
                            2. Contrato - CCB
                          </Link>
                        </HStack>
                        <HStack spacing={2} align="flex-start">
                          <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.700">•</Text>
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadDocument(
                                "termos-e-condicoes-gerais",
                                selectedOfferId,
                                insuranceInfo?.id || undefined
                              );
                            }}
                            textDecoration="underline"
                            color="green.500"
                            fontWeight="semibold"
                            cursor="pointer"
                            fontSize={{ base: "xs", sm: "sm" }}
                          >
                            3. Termos e Condições Gerais
                          </Link>
                        </HStack>
                      </VStack>
                    ) : (
                      <HStack spacing={2} align="flex-start">
                        <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.700">•</Text>
                        <Link
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownloadDocument(
                              "contrato-ccb",
                              selectedOfferId
                            );
                          }}
                          textDecoration="underline"
                          color="green.500"
                          fontWeight="semibold"
                          cursor="pointer"
                          fontSize={{ base: "xs", sm: "sm" }}
                        >
                          1. Contrato - CCB
                        </Link>
                      </HStack>
                    )}
                  </VStack>
                </HStack>
              </VStack>
            );
          })()}
          <Button
          isDisabled={
            !selectedOfferId ||
            !isOfferReadyToSign(selectedOfferId) ||
            isAnySubmitting
          }
          isLoading={isAnySubmitting}
          onClick={() => {
            if (selectedOfferId) {
              const selectedOffer = sortedFinancialOffers.find(
                (offer) => offer.id === selectedOfferId
              );
              if (selectedOffer) {
                handleAssinar(selectedOffer.id, selectedOffer.comSeguro);
              }
            }
          }}
          bg="primary"
          color="white"
            width="100%"
          borderRadius="3xl"
          py={6}
          mt={2}
          mb={4}
            fontWeight="bold"
          fontSize={{ base: "sm", sm: "md", md: "lg" }}
          _hover={{
            bg: "primary",
            filter: "brightness(110%)",
          }}
          _disabled={{
            bg: "gray.300",
            color: "gray.500",
            cursor: "not-allowed",
            opacity: 0.6,
          }}
          >
            Assinar
          </Button>
        </VStack>
    </Box>
  );
}
